import * as React from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Stack,
  Collapse,
  Icon,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useDisclosure,
} from '@chakra-ui/react';
import {
  IoMenu,
  IoClose,
  IoChevronDown,
  IoChevronForward,
} from 'react-icons/io5';
import { StaticImage } from 'gatsby-plugin-image';
import { Link as GatsbyLink } from 'gatsby';

export function Navbar() {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Box>
      <Flex
        bg="white"
        color="surface.on"
        minH="16"
        py="2"
        px="4"
        borderBottom={1}
        borderStyle="solid"
        borderColor="gray.200"
        align="space-between"
      >
        <Flex flex="1">
          <StaticImage src="../images/logo.png" alt="Yaisana" height={48} />
          <Flex display={{ base: 'none', md: 'flex' }} ml="4">
            <DesktopNav />
          </Flex>
        </Flex>
        <Flex display={{ base: 'flex', md: 'none' }} alignItems="center">
          <IconButton
            onClick={onToggle}
            icon={<Icon as={isOpen ? IoClose : IoMenu} w="5" h="5" />}
            variant="ghost"
            aria-label="Toggle Navigation"
          />
        </Flex>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav />
      </Collapse>
    </Box>
  );
}

const DesktopNav = () => {
  const linkColor = 'gray.600';
  const linkHoverColor = 'primary.main';
  const popoverContentBgColor = 'white';

  return (
    <Stack direction="row" spacing={4} alignItems="center">
      {ROUTES.map((navItem) => (
        <Box key={navItem.label}>
          <Popover trigger="hover" placement="bottom-start">
            <PopoverTrigger>
              <Link
                p="2"
                fontSize="sm"
                fontWeight="normal"
                color={linkColor}
                _hover={{
                  textDecoration: 'none',
                  color: linkHoverColor,
                }}
                as={GatsbyLink}
                to={navItem.href ?? '#'}
              >
                {navItem.label}
              </Link>
            </PopoverTrigger>

            {navItem.children && (
              <PopoverContent
                border={1}
                boxShadow="xl"
                bg={popoverContentBgColor}
                p="4"
                rounded="xl"
                minW="sm"
              >
                <Stack>
                  {navItem.children.map((child) => (
                    <DesktopSubNav key={child.label} {...child} />
                  ))}
                </Stack>
              </PopoverContent>
            )}
          </Popover>
        </Box>
      ))}
    </Stack>
  );
};

const DesktopSubNav = ({ label, href }: NavItem) => {
  return (
    <Link
      role="group"
      display="block"
      p="2"
      rounded="md"
      _hover={{ bg: 'primary.container' }}
      as={GatsbyLink}
      to={href ?? '#'}
    >
      <Stack direction="row" align="center">
        <Box>
          <Text
            transition="all .3s ease"
            _groupHover={{ color: 'primary.main' }}
            fontWeight="normal"
          >
            {label}
          </Text>
        </Box>
        <Flex
          transition="all .3s ease"
          transform="translateX(-10px)"
          opacity="0"
          _groupHover={{ opacity: '100%', transform: 'translateX(0)' }}
          justify="flex-end"
          align="center"
          flex="1"
        >
          <Icon color="primary.main" w="5" h="5" as={IoChevronForward} />
        </Flex>
      </Stack>
    </Link>
  );
};

const MobileNav = () => {
  return (
    <Stack bg="white" p="4" display={{ md: 'none' }} 
    borderBottom={1}
    borderStyle="solid"
    borderColor="gray.200">
      {ROUTES.map((navItem) => (
        <MobileNavItem key={navItem.label} {...navItem} />
      ))}
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href }: NavItem) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Stack spacing="4" onClick={children && onToggle}>
      <Flex
        py="2"
        justify="space-between"
        align="center"
        _hover={{
          textDecoration: 'none',
        }}
        as={GatsbyLink}
        to={href ?? '#'}
      >
        <Text fontWeight="bold" color="surface.on">
          {label}
        </Text>
        {children && (
          <Icon
            as={IoChevronDown}
            transition="all .25s ease-in-out"
            transform={isOpen ? 'rotate(180deg)' : ''}
            w="5"
            h="5"
          />
        )}
      </Flex>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: '0!important' }}>
        <Stack
          mt="2"
          pl="4"
          borderLeft={1}
          borderStyle="solid"
          borderColor="gray.200"
          align="start"
        >
          {children &&
            children.map((child) => (
              <Link
                key={child.label}
                py={2}
                as={GatsbyLink}
                to={child.href ?? '#'}
              >
                {child.label}
              </Link>
            ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

interface NavItem {
  label: string;
  subLabel?: string;
  children?: Array<NavItem>;
  href?: string;
}

const ROUTES: Array<NavItem> = [
  {
    label: 'Home',
    href: '/',
  },
  {
    label: 'Services',
    children: [
      {
        label: 'Social Media Strategy',
        href: '/services/social-media-strategy',
      },
      {
        label: 'Social Media Management',
        href: '/services/social-media-management',
      },
      {
        label: 'Paid Social Advertising',
        href: '/services/paid-social-advertising',
      },
      {
        label: 'Data Analysis and ROI Modeling',
        href: '/services/data-analysis',
      },
      {
        label: 'Social Selling',
        href: '/services/social-selling',
      },
    ],
  },
  {
    label: 'Studio',
    href: '/services/social-content-production',
  },
  {
    label: 'About Us',
    href: '/about',
  },
  {
    label: 'Contact',
    href: '/contact',
  },
];
